import { Add as AddIcon } from "@mui/icons-material";
import { Autocomplete, Box, Button, Chip, TextField, Typography } from "@mui/material";
import React, { useMemo, useState } from "react";

import { useCapabilityMap } from "../../../hooks/queries/useCapabilityMap";
import { CapabilityMapParams } from "../../../services/productPlaneApi";
import { CapabilityMapNode, Matrix } from "../../../types/analytics";
import { MAX_CHOSEN_CAPABILITIES, MatrixSettings } from "../analyticsState";
import { CUSTOM } from "./matrixPresets";
import { axisAbilities } from "./matrixModel";

// Most capabilities the picker offers: the most claimed of the scope
export const PICKER_LIMIT = 200;

interface PickerOption {
  id: string;
  name: string;
  area: string;
  count: number;
}

interface MatrixCapabilitiesProps {
  matrix: Matrix;
  // The selection as the capability map reads it, for the picker's options
  scope: CapabilityMapParams;
  onChange: (change: Partial<MatrixSettings>) => void;
}

// The abilities of the capability axis as chips: a chip's cross takes its
// ability away, the picker adds one. Either turns the set into a custom set
// of the abilities shown, so that a preset is the start of one's own.
const MatrixCapabilities: React.FC<MatrixCapabilitiesProps> = ({ matrix, scope, onChange }) => {
  const shown = useMemo(() => axisAbilities(matrix), [matrix]);
  const [adding, setAdding] = useState(false);
  const params = useMemo<CapabilityMapParams>(
    () => ({ ...scope, grain: undefined, top_n: PICKER_LIMIT }),
    [scope],
  );
  const map = useCapabilityMap(params, adding);
  const options = useMemo<PickerOption[]>(() => {
    const taken = new Set(shown.map((ability) => ability.id));
    const nodes: CapabilityMapNode[] = map.data?.nodes ?? [];
    return nodes
      .filter((node) => !taken.has(node.capability.id))
      .map(
        (node): PickerOption => ({
          id: node.capability.id,
          name: node.capability.name ?? node.capability.id,
          area: node.effect_area?.name ?? "Other",
          count: node.product_count,
        }),
      )
      .sort((a, b) => a.area.localeCompare(b.area) || b.count - a.count);
  }, [map.data, shown]);

  const choose = (abilities: string[]) =>
    onChange({ capabilitySet: CUSTOM, capabilities: abilities });
  const full = shown.length >= MAX_CHOSEN_CAPABILITIES;
  const noun = matrix.columns === "capabilities" ? "Columns" : "Rows";

  return (
    <Box
      role="group"
      aria-label="Capabilities shown"
      sx={{ display: "flex", alignItems: "center", gap: 0.75, flexWrap: "wrap" }}
    >
      <Typography component="span" sx={{ color: "text.disabled", fontSize: 12.5, mr: 0.25 }}>
        {noun}
      </Typography>
      {shown.map((ability) => (
        <Chip
          key={ability.id}
          size="small"
          variant="outlined"
          label={ability.name}
          // The last one stays: a set of none would be no table
          onDelete={
            shown.length > 1
              ? () => choose(shown.filter((other) => other.id !== ability.id).map((other) => other.id))
              : undefined
          }
          deleteIcon={<span aria-label={`Remove ${ability.name}`}>×</span>}
          sx={{ fontSize: 12 }}
        />
      ))}
      {adding ? (
        <Autocomplete<PickerOption>
          size="small"
          openOnFocus
          autoHighlight
          options={options}
          loading={map.isLoading}
          groupBy={(option) => option.area}
          getOptionLabel={(option) => option.name}
          onChange={(_event, picked) => {
            if (picked) {
              setAdding(false);
              choose([...shown.map((ability) => ability.id), picked.id]);
            }
          }}
          onClose={(_event, reason) => {
            if (reason === "escape" || reason === "blur") {
              setAdding(false);
            }
          }}
          renderOption={(props, option) => (
            <li {...props} key={option.id}>
              <Box sx={{ display: "flex", justifyContent: "space-between", width: "100%", gap: 1 }}>
                <span>{option.name}</span>
                <Typography component="span" color="text.disabled" sx={{ fontSize: 12 }}>
                  {option.count}
                </Typography>
              </Box>
            </li>
          )}
          noOptionsText="No other capability is claimed in this scope"
          sx={{ width: 280 }}
          renderInput={(inputParams) => (
            <TextField
              {...inputParams}
              autoFocus
              placeholder="Find a capability"
              inputProps={{ ...inputParams.inputProps, "aria-label": "Add a capability" }}
            />
          )}
        />
      ) : (
        <Button
          size="small"
          startIcon={<AddIcon sx={{ fontSize: 16 }} />}
          disabled={full}
          onClick={() => setAdding(true)}
          title={full ? `At most ${MAX_CHOSEN_CAPABILITIES} capabilities` : undefined}
          sx={{ textTransform: "none", fontSize: 12.5 }}
        >
          Add capability
        </Button>
      )}
    </Box>
  );
};

export default MatrixCapabilities;
